import TextInput from 'components/Common/Form/Input/TextInput'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import useKeyPress from 'hooks/useKeyPress'
import { EmptyArray } from 'lib/array/arrayUtils'
import type { ComponentProps } from 'react'
import { useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import AirportDropdownOption from './AirportDropdownOption'
import NoAirportsOptionListItem from './NoAirportsOptionListItem'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'

const MAX_VISIBLE_OPTIONS = 7

interface Props extends Omit<Pick<ComponentProps<typeof DropdownList>, 'anchorRef' | 'triggerRef' | 'open' | 'onClose' | 'size' | 'width' | 'portaled'>, 'size' | 'width'> {
  id?: string
  loading?: boolean
  airports?: Array<App.AirportLocation>
  inputValue?: string
  heading?: string
  airportIcon?: React.ReactNode
  selectedAirport?: App.AirportLocation
  enableSearchBox?: boolean
  onSelectAirport: (airport: App.AirportLocation) => void
  onChangeText?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placement?: ComponentProps<typeof DropdownList>['placement']
  anchorOffset?: ComponentProps<typeof DropdownList>['anchorOffset']
  size?: ComponentProps<typeof DropdownList>['size']
  width?: ComponentProps<typeof DropdownList>['width']
}

function AirportSelectDropdown(props: Props) {
  const {
    anchorRef,
    triggerRef,
    id,
    open,
    size = 'fill-anchor',
    width,
    placement,
    portaled,
    anchorOffset,
    loading,
    onClose,
    airports = EmptyArray,
    onSelectAirport,
    inputValue = '',
    heading,
    airportIcon,
    selectedAirport,
    enableSearchBox,
    onChangeText,
  } = props

  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [cursor, setCursor] = useState<number>(-1)
  const [downPress, upPress, enterPress] = useKeyPress(['ArrowDown', 'ArrowUp', 'Enter'])
  const wrapperRef = useRef<HTMLDivElement>(null)
  const airportsCount = airports.length

  useEffect(() => {
    if (!open) return
    setCursor(airports.findIndex(airport => airport.airportCode === selectedAirport?.airportCode))
  }, [airports, open, selectedAirport?.airportCode])

  useEffect(() => {
    if (!open) return

    // change selected airport option by up & down arrow keys
    if (airportsCount && downPress) {
      const newCursor = cursor < airportsCount - 1 ? cursor + 1 : cursor
      setCursor(newCursor)

      // scroll down
      if (newCursor >= MAX_VISIBLE_OPTIONS) {
        wrapperRef.current?.scrollBy({ top: 58, behavior: 'smooth' })
      }
    }

    if (airportsCount && upPress) {
      const newCursor = cursor > 0 ? cursor - 1 : cursor
      setCursor(newCursor)

      // scroll up
      if (newCursor < (airportsCount - MAX_VISIBLE_OPTIONS)) {
        wrapperRef.current?.scrollBy({ top: -58, behavior: 'smooth' })
      }
    }

    if (enterPress && cursor >= 0 && airports[cursor]) {
      onSelectAirport(airports[cursor])
    }
  }, [downPress, upPress, enterPress]) // oxlint-disable-line react-hooks/exhaustive-deps

  const noAirportsAvailable = !loading && airports.length === 0 && inputValue.length > 2

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    }
  }, [open])

  return (
    <DropdownList
      placement={placement}
      portaled={portaled}
      anchorOffset={anchorOffset}
      size={size}
      width={width}
      open={!!open}
      anchorRef={anchorRef}
      triggerRef={triggerRef}
      onClose={onClose}
      title={heading}
      data-testid="airport-select-dropdown"
      headerExtension={enableSearchBox && <TextInput
        placeholder={intl.formatMessage({
          id: 'mqMo_fmGhVuXciY5Cv0L',
          defaultMessage: 'Search by city or airport',
          description: 'Placeholder text for airport search input in the flights flow',
        })}
        value={inputValue}
        onChange={onChangeText}
        ref={inputRef}
        endIcon={<LineSearchIcon />}
        noValidationSpacing
        noValidationMessage
      />}
    >
      <div ref={wrapperRef}>
        {loading && <>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <ListItemLoadingSkeleton size="medium" title="50%" subtitle="30%" startIcon />
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <ListItemLoadingSkeleton size="medium" title="40%" subtitle="60%" startIcon />
        </>}
        {!loading && airports?.map((airport, index) =>
          <AirportDropdownOption
            key={airport.id ?? airport.airportCode}
            id={id}
            subRecord={airport.isCityAirport}
            airport={airport}
            onSelectAirport={onSelectAirport}
            selected={cursor === index}
            icon={airportIcon}
          />,
        )}
        {noAirportsAvailable && <NoAirportsOptionListItem inputValue={inputValue} />}
      </div>
    </DropdownList>
  )
}

export default AirportSelectDropdown
